import {
    Button,
    Link,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material'
import React from 'react'
import { useAppLocale } from '../../../i18n/localeUtils'
import { Archive, Check, Repeat } from 'react-feather'
import { NavLink } from 'react-router'
import { formatDateYYYYMMDD } from '../../../packages/utils/numberFormatters'
import { OutlinedIconButton } from './OutlinedIconButton'
import { PRInputListItem } from '@finance-lab/utils/types'
import { CompanyEntity } from 'types/CompanyEntity'
import { getPressReleaseLink } from '../../../utils/get-press-release-link'

export type PRTableProps = {
    onArchive?: (pr: PRInputListItem) => Promise<void>
    onBlock?: (pr: PRInputListItem) => Promise<void>
    onUnarchive?: (pr: PRInputListItem) => void
    prs: PRInputListItem[] | null
    selectedPR?: PRInputListItem | null
    setSelectedCompany?: (company: Pick<CompanyEntity, 'id' | 'name'>) => void
    setSelectedPR?: (pr: PRInputListItem | null) => void
}

export const PRTable = ({
    prs,
    onArchive,
    onUnarchive,
    setSelectedCompany: selectTargetCompany,
    selectedPR,
    setSelectedPR,
}: PRTableProps) => {
    const theme = useTheme()
    const { language: locale } = useAppLocale()
    const onSelectPR =
        setSelectedPR &&
        selectTargetCompany &&
        ((pr: PRInputListItem) => {
            // select the PR
            setSelectedPR(pr)

            // if the PR has a company, select the company
            if (pr.companyId) {
                selectTargetCompany({ id: pr.companyId, name: pr.companyName })
            }
        })

    if (prs === null || prs === undefined) {
        return (
            <>
                <Skeleton variant='rectangular' height={24 * 4} sx={{ my: 6 }} />
                <Skeleton variant='rectangular' height={24 * 4} sx={{ my: 6 }} />
                <Skeleton variant='rectangular' height={24 * 4} sx={{ my: 6 }} />
                <Skeleton variant='rectangular' height={24 * 4} sx={{ my: 6 }} />
            </>
        )
    }
    if (prs.length === 0) {
        return <Typography variant='body1'>Inga pressmeddelanden hittades</Typography>
    }

    const rows = prs.map((pr) => (
        <TableRow
            key={pr.id}
            sx={{
                backgroundColor: pr.id === selectedPR?.id ? theme.palette.rowColor.main : null,
            }}
        >
            <TableCell>
                <Stack spacing={2}>
                    <Typography variant='caption'>
                        {formatDateYYYYMMDD(pr.publishedAt, { locale })} {pr.companyName}
                    </Typography>
                    <Link component={NavLink} to={getPressReleaseLink(pr.companyId, pr.id)} target='_blank'>
                        {pr.title || pr.id || 'Titel saknas'}
                    </Link>
                </Stack>
            </TableCell>
            <TableCell>
                <Stack spacing={2}>
                    {onSelectPR && (
                        <Button
                            variant='contained'
                            color='primary'
                            size='small'
                            onClick={() => onSelectPR(pr)}
                            endIcon={<Check size={16} />}
                        >
                            Välj
                        </Button>
                    )}
                    {onArchive && (
                        <Button
                            variant='outlined'
                            color='primary'
                            size='small'
                            onClick={() => void onArchive(pr)}
                            endIcon={<Archive size={16} />}
                        >
                            Arkiv
                        </Button>
                    )}

                    {onUnarchive && (
                        <Tooltip title='Återställ'>
                            <OutlinedIconButton color='primary' size='small' onClick={() => onUnarchive(pr)}>
                                <Repeat size={16} />
                            </OutlinedIconButton>
                        </Tooltip>
                    )}
                </Stack>
            </TableCell>
        </TableRow>
    ))

    return (
        <>
            <TableContainer>
                <Table>
                    <TableBody>{rows}</TableBody>
                </Table>
            </TableContainer>
        </>
    )
}
